import useRouteName from "../useRouteName";
import { useHistory } from "react-router-dom";
import useCreateUrl from "../useCreateUrl";
import { useScreenUrl } from "../../app/navigation";
import { useCallback } from "react";
import { PRE_OPTED_ANCILLARIES_QUERY_PARAMS } from "../../constants";
import useAvailableCheckoutSteps from "./useAvailableCheckoutSteps";
import useSendTravelProductBookingProcessC360Event from "../c360/useSendTravelProductBookingProcessC360Event";
import useCheckoutSteps from "./useCheckoutSteps";
import { RouteName } from "@flights/constants";

type Params = {
  action?: "push" | "replace";
  locationState?: Record<string, any>;
};

const useNextCheckoutScreen = () => {
  const steps = useAvailableCheckoutSteps();
  const stepsName = steps.map(({ step }) => step);
  const currentStep = useRouteName();
  const nextCheckoutStep = getNextCheckoutStep(stepsName, currentStep);
  const history = useHistory();
  const { createUrl } = useCreateUrl();
  const { getScreenUrl: getNextCheckoutScreenUrl } = useScreenUrl(nextCheckoutStep, {
    doNotThrowErrorWithoutTriggeringAction: true
  });
  const sendTravelProductBookingProcessC360Event = useSendTravelProductBookingProcessC360Event();
  const { isFirstStepAfterStart } = useCheckoutSteps();

  const goNextCheckoutScreen = useCallback(
    (params?: Params) => {
      if (!nextCheckoutStep) {
        throw new Error(`Next checkout step doesn't exist. Current route: ${currentStep}`);
      }

      let url = createUrl(getNextCheckoutScreenUrl());

      if (nextCheckoutStep === "checkout-pax") {
        const newUrl = new URL(window.location.origin + url);
        newUrl.searchParams.delete(PRE_OPTED_ANCILLARIES_QUERY_PARAMS.flexibleTicket);
        url = newUrl.pathname + newUrl.search;
      }

      sendTravelProductBookingProcessC360Event();

      const navigationAction = params?.action ?? (isFirstStepAfterStart ? "push" : "replace");

      history[navigationAction](url, params?.locationState);
    },
    [
      nextCheckoutStep,
      createUrl,
      getNextCheckoutScreenUrl,
      sendTravelProductBookingProcessC360Event,
      isFirstStepAfterStart,
      history,
      currentStep
    ]
  );

  const _getNextCheckoutScreenUrl = useCallback(() => {
    if (!nextCheckoutStep) {
      throw new Error(`Next checkout step doesn't exist. Current route: ${currentStep}`);
    }

    return getNextCheckoutScreenUrl;
  }, [currentStep, getNextCheckoutScreenUrl, nextCheckoutStep]);

  return {
    getNextCheckoutScreenUrl: _getNextCheckoutScreenUrl,
    goNextCheckoutScreen
  };
};

const getNextCheckoutStep = (steps: RouteName[], currentStep: RouteName) => {
  const nextStep = steps[steps.indexOf(currentStep) + 1];

  return nextStep;
};

export default useNextCheckoutScreen;
